// §V2 EXACT PORT — Account (docs/design-spikes/pomela-platform-v2/index.html
// lines 1086-1178): profile panel (real identity + real stat grid), Pro
// gradhero → the existing Upgrade flow, credits + payments = the REAL money
// components rendered UNMODIFIED (CreditPanel / BugBountyPanel /
// PaymentConnect — §money guardrail: never re-implement billing), per-app
// usage from GET /v1/analytics, and the danger zone wired to the REAL
// sign-out + typed-"delete" erasure flow ported from screens/Account.tsx.
// The spike's data-statehost stays, but the state is driven by the REAL
// auth + GET /v1/credits lifecycle (same fetch V2Shell's rail meter uses),
// not the prototype's .state-switch togglers.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { useAuth } from '@vibeshell/hooks';
import { Ic, tt } from './icons';
import { t, getLocale } from '../lib/i18n';
import { goNav } from '../lib/nav-items';
import { CreditPanel } from '../components/CreditPanel';
import { BugBountyPanel } from '../components/BugBountyPanel';
import { PaymentConnect } from '../components/PaymentConnect';
import { getDeviceId } from '../lib/deviceId';
import { clearStreak } from '../lib/streak';
import { emitPomelaToast } from '../lib/toast-bridge';
import { performSignOut, broadcastSignOut } from './session-signout';

// ─── the exact data plumbing Account/AppAnalytics use (same endpoints) ──────

/** GET /v1/analytics — account-scoped per-app numbers (AppAnalytics' shape). */
interface UsageRow {
  appId: string;
  name: string;
  interactions: number;
  creditsUsed: number;
  aiCostUsd: number;
  views: number;
  installs: number;
}
interface UsageTotals {
  apps: number;
  interactions: number;
  creditsUsed: number;
  views: number;
  installs: number;
}
type Usage = { s: 'loading' } | { s: 'ready'; apps: UsageRow[]; totals: UsageTotals } | { s: 'error' };

/** "May" + "2026" from the REAL memberSince timestamp (Account.tsx's
 *  fmtDate, split for the spike's s-num / s-lbl composition). */
function joinedParts(ms: number | undefined): { month: string; year: string } | null {
  if (!ms) return null;
  const loc = getLocale() === 'he' ? 'he-IL' : 'en-US';
  const d = new Date(ms);
  return { month: d.toLocaleDateString(loc, { month: 'short' }), year: String(d.getFullYear()) };
}

export function AccountV2(): JSX.Element {
  const { session, loading, signOut, refresh } = useAuth();
  const rootRef = useRef<HTMLElement>(null);

  // §LG-S4 billing-reachability fix — the signed-in Account body (cancel-Pro,
  // GDPR account delete, sign-out, CreditPanel/BugBounty/PaymentConnect) must
  // NEVER be blanked or delayed by a GET /v1/credits hiccup. CreditPanel and
  // V2Shell's rail-meter each own their own /v1/credits fetch + inline error,
  // exactly as screens/Account.tsx does — so this screen no longer gates its
  // whole body behind a credits probe. (Previously a failed /v1/credits hid
  // subscription-cancel and account-deletion — a billing/compliance regression.)

  // ── per-app usage — REAL GET /v1/analytics (AppAnalytics' exact source) ──
  const [usage, setUsage] = useState<Usage>({ s: 'loading' });
  const [usageTick, setUsageTick] = useState(0);
  useEffect(() => {
    if (loading || !session.signedIn) return;
    let alive = true;
    setUsage({ s: 'loading' });
    fetch('/v1/analytics', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('http'))))
      .then((data: { apps?: UsageRow[]; totals?: UsageTotals }) => {
        if (!alive) return;
        setUsage({
          s: 'ready',
          apps: data.apps ?? [],
          totals: data.totals ?? { apps: 0, interactions: 0, creditsUsed: 0, views: 0, installs: 0 }
        });
      })
      .catch(() => {
        if (alive) setUsage({ s: 'error' });
      });
    return () => {
      alive = false;
    };
  }, [loading, session.signedIn, session.accountId, usageTick]);

  // ── #account?upgraded=1 return path — Account.tsx's exact flow: refresh
  //    the plan from the server FIRST; the banner renders only when the
  //    settled plan is actually 'pro' (never a false "Welcome to Pro"). ─────
  const [justUpgraded, setJustUpgraded] = useState(false);
  const [refreshSettled, setRefreshSettled] = useState(false);
  useEffect(() => {
    if (!location.hash.includes('upgraded=1')) return;
    let cancelled = false;
    const safety = window.setTimeout(() => {
      if (!cancelled) setRefreshSettled(true);
    }, 1500);
    void refresh().then(() => {
      window.clearTimeout(safety);
      if (!cancelled) setRefreshSettled(true);
    });
    history.replaceState(null, '', location.pathname + location.search + '#account');
    return () => {
      cancelled = true;
      window.clearTimeout(safety);
    };
  }, [refresh]);
  useEffect(() => {
    if (refreshSettled && session.plan === 'pro') setJustUpgraded(true);
  }, [refreshSettled, session.plan]);

  // ── plan management — the legacy PlanSection's exact cancel flow (confirm
  //    → POST /v1/billing/cancel → refresh) so upg.refund1's "cancel in one
  //    tap from Account" stays TRUE. Same endpoint + catalog copy; §money
  //    guardrail: reachability restored, no billing semantics changed. ──────
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelErr, setCancelErr] = useState(false);
  async function doCancelPro(): Promise<void> {
    setCancelling(true);
    setCancelErr(false);
    // §money-honesty (2026-07-12) — a cancel that FAILS must NOT silently collapse the confirm UI back
    // to "● Active" as if nothing happened (the user tapped Cancel Pro, saw no error, and is still Pro
    // with no way to tell whether it worked). Gate the collapse on a real 2xx; on any failure surface an
    // honest error and KEEP the confirm UI open so the user can retry.
    let ok = false;
    try {
      const res = await fetch('/v1/billing/cancel', { method: 'POST', credentials: 'include' });
      ok = res.ok;
    } catch {
      ok = false;
    }
    setCancelling(false);
    if (ok) {
      setConfirmCancel(false);
    } else {
      setCancelErr(true);
    }
    void refresh();
  }

  // ── danger zone — the REAL flows from screens/Account.tsx ────────────────
  const [confirmingOut, setConfirmingOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  async function doSignOut(): Promise<void> {
    setSigningOut(true);
    // performSignOut hits the REAL endpoint via the hook and, on success,
    // broadcasts to sibling tabs. Honest failure: keep the user here with an
    // error toast rather than silently pretending the sign-out worked.
    const ok = await performSignOut(signOut);
    setSigningOut(false);
    if (ok) {
      goNav('');
    } else {
      emitPomelaToast('error', tt('v2.signout.error', 'Couldn’t sign you out — check your connection and try again.'));
    }
  }

  /** Best-effort local wipe — Account.tsx's exact routine. */
  function wipeLocalState(): void {
    try {
      const preserveKeys = ['vs:creator:deviceId']; // keep the device id so re-signup pairs cleanly
      const keep = preserveKeys
        .map((k) => [k, localStorage.getItem(k)] as const)
        .filter(([, v]) => v !== null);
      localStorage.clear();
      keep.forEach(([k, v]) => v !== null && localStorage.setItem(k, v));
      clearStreak();
    } catch {
      /* storage blocked — at least the sign-out below will detach the session */
    }
  }

  /** Two-step deletion (confirm token → execute) — Account.tsx's exact
   *  hard-fail contract: network failure, 404, 5xx all surface back to the
   *  user; local state is wiped only AFTER the server confirms. */
  async function doDelete(): Promise<void> {
    setDeleting(true);
    setDeleteError('');
    try {
      const deviceId = getDeviceId();
      const confirmRes = await fetch('/v1/me/delete/confirm', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ deviceId })
      }).catch(() => null);

      if (!confirmRes) {
        setDeleting(false);
        setDeleteError(t('acct.delErrConn'));
        return;
      }
      if (confirmRes.status === 404 || confirmRes.status >= 500) {
        setDeleting(false);
        setDeleteError(t('acct.delErrService'));
        return;
      }
      if (!confirmRes.ok) {
        setDeleting(false);
        setDeleteError(
          confirmRes.status === 401 || confirmRes.status === 403 ? t('acct.delErrExpired') : t('acct.delErrStart')
        );
        return;
      }
      const { token } = (await confirmRes.json().catch(() => ({}))) as { token?: string };
      if (!token) {
        setDeleting(false);
        setDeleteError(t('acct.delErrResponse'));
        return;
      }
      const execRes = await fetch(
        `/v1/me/delete?deviceId=${encodeURIComponent(deviceId)}&token=${encodeURIComponent(token)}`,
        { method: 'DELETE', credentials: 'include' }
      ).catch(() => null);
      if (!execRes || !execRes.ok) {
        setDeleting(false);
        setDeleteError(t('acct.delErrFinish'));
        return;
      }
      // Server-side delete succeeded — only now wipe local state + sign out.
      wipeLocalState();
      await signOut().catch(() => null);
      broadcastSignOut(); // sibling tabs must drop to signed-out too
      setDeleting(false);
      setDeleteOpen(false);
      goNav('');
    } catch {
      setDeleting(false);
      setDeleteError(t('acct.delErrConn2'));
    }
  }

  // The spike's prototype .state-switch is dropped — the statehost state is
  // the REAL auth + credits lifecycle.
  // Body renders as soon as auth resolves — independent of the credits fetch.
  const state: 'loading' | 'loaded' = loading ? 'loading' : 'loaded';

  // Scroll-reveal — the spike's exact observer (root = #canvas, .12 / -6%).
  useEffect(() => {
    if (state !== 'loaded') return;
    const rootEl = rootRef.current;
    if (!rootEl) return;
    if (!('IntersectionObserver' in window)) {
      rootEl.querySelectorAll('.sr,.sr-stagger').forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      },
      { root: document.getElementById('canvas'), threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    const raf = requestAnimationFrame(() => {
      rootEl.querySelectorAll('.sr:not(.in),.sr-stagger:not(.in)').forEach((el) => io.observe(el));
    });
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, [state, session.signedIn]);

  const isPro = session.plan === 'pro';
  const joined = joinedParts(session.memberSince);
  const usageRow = (a: UsageRow): string =>
    tt('v2.account.usageRow', '{n} AI uses · {c} credits')
      .replace('{n}', a.interactions.toLocaleString())
      .replace('{c}', a.creditsUsed.toLocaleString());

  const canConfirmDelete = deleteTyped.trim().toLowerCase() === 'delete' && !deleting;

  return (
    <section className="screen on" data-screen="account" ref={rootRef}>
      <div className="wrap-narrow stack">
        <div className="screen-head">
          <div className="sh-grow">
            <h2 className="title-page">{t('sb.account.title')}</h2>
          </div>
          {/* the spike's .state-switch is prototype-only — prod states are the real fetches */}
          {state === 'loaded' && session.signedIn && isPro && (
            <span className="badge pro">
              <Ic n="spark" /> {tt('v2.account.pro', 'Pro')}
            </span>
          )}
        </div>

        <div data-statehost="account" data-state={state}>
          {/* LOADED */}
          {state === 'loaded' && (
            <div data-when="loaded" className="stack">
              {!session.signedIn ? (
                /* signed-out — honest sign-in panel (the spike modeled a
                   signed-in Pro account only) */
                <div className="panel empty sr">
                  <span className="e-ic">
                    <Ic n="user" />
                  </span>
                  <h4>{t('set.notSignedIn')}</h4>
                  <p>{t('acc.signInSync')}</p>
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => {
                      location.hash = 'signin';
                    }}
                  >
                    {t('acc.signIn')}
                  </button>
                </div>
              ) : (
                <>
                  {/* checkout return banner — real refresh-gated flow (never fake Pro) */}
                  {justUpgraded && (
                    <div className="panel panel-p">
                      <div className="row" style="gap:12px">
                        <span className="l-ico brand">
                          <Ic n="spark" />
                        </span>
                        <div style="min-width:0">
                          <b style="font-size:14px;display:block">{t('pw.welcome')}</b>
                          <div className="sub-h" style="margin-top:2px">
                            {t('acc.upgraded')}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* profile panel — REAL session identity + REAL stat grid */}
                  <div className="panel panel-p sr">
                    <div className="row" style="gap:14px;margin-bottom:18px">
                      <span className="ava ava-lg">{(session.email ?? '?').charAt(0).toUpperCase()}</span>
                      <div style="min-width:0">
                        <b style="font-size:16px;display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
                          {session.email}
                        </b>
                        <div className="sub-h" style="margin-top:2px;text-transform:capitalize">
                          {t('acc.signInVia', { p: session.provider ?? 'magic-link' })}
                        </div>
                      </div>
                    </div>
                    <div className="stat-grid sg-3">
                      <div className="stat">
                        <div className="s-num">{(session.appsCreated ?? 0).toLocaleString()}</div>
                        <div className="s-lbl">{tt('v2.account.statApps', 'Apps built (lifetime)')}</div>
                      </div>
                      <div className="stat">
                        <div className="s-num">{(session.devices ?? 1).toLocaleString()}</div>
                        <div className="s-lbl">{t('acc.devices')}</div>
                      </div>
                      <div className="stat">
                        <div className="s-num">{joined ? joined.month : '—'}</div>
                        <div className="s-lbl">
                          {joined
                            ? tt('v2.account.statJoined', 'Joined {y}').replace('{y}', joined.year)
                            : t('acc.joined')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pro gradhero — spike copy. Free → the Upgrade flow (it owns
                      pricing). Pro → the legacy PlanSection's in-place cancel
                      (confirm → POST /v1/billing/cancel → refresh). */}
                  <div className="gradhero sr" style="padding:20px">
                    <div className="row" style="gap:10px">
                      <span className="row gap8" style="font-weight:800;font-size:17px">
                        <Ic n="spark" /> {t('upg.proBadge')}
                      </span>
                      {isPro && <span className="badge on-grad-chip">● {t('acc.active')}</span>}
                    </div>
                    <p style="margin-top:8px">{isPro ? t('acc.proAll') : t('acc.proFeat')}</p>
                    {!isPro ? (
                      <div className="gh-link">
                        <button className="btn btn-solid" type="button" onClick={() => goNav('upgrade')}>
                          {t('cr.upgradeCta')}
                        </button>
                      </div>
                    ) : !confirmCancel ? (
                      <div className="gh-link">
                        <button className="btn btn-solid" type="button" onClick={() => setConfirmCancel(true)}>
                          {t('acc.managePlan')}
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* first-party catalog string — same html the legacy PlanSection rendered */}
                        <p
                          style="margin-top:10px;font-size:12.5px;line-height:1.55;color:rgba(255,255,255,.9)"
                          dangerouslySetInnerHTML={{ __html: t('acct.cancelInfo') }}
                        />
                        <div className="row" style="gap:8px;margin-top:12px">
                          <button
                            className="btn btn-ghost"
                            style="background:rgba(0,0,0,.28);color:#fff"
                            type="button"
                            disabled={cancelling}
                            onClick={() => void doCancelPro()}
                          >
                            {cancelling ? t('acct.cancelling') : t('acct.cancelPro')}
                          </button>
                          <button className="btn btn-solid" type="button" onClick={() => setConfirmCancel(false)}>
                            {t('acct.keepPro')}
                          </button>
                        </div>
                        {cancelErr && (
                          <p className="sub-h" role="alert" style="margin:8px 0 0;color:var(--danger)">
                            {t('acct.cancelFailed')}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* credits + bounty + payments — the REAL money surfaces,
                      rendered UNMODIFIED (§money guardrail). CreditPanel IS the
                      spike's credits panel with real flows: cr.title/cr.sub/
                      cr.left/cr.buyMore/cr.ownKey are the spike's exact strings
                      plus the real top-up checkout + BYOK. PaymentConnect IS the
                      spike's payments panel (pc.title/pc.sub = spike copy) with
                      the real processor-connect flow. */}
                  <div className="sr">
                    <CreditPanel />
                  </div>
                  <div className="sr">
                    <BugBountyPanel />
                  </div>
                  <div className="sr">
                    <PaymentConnect />
                  </div>

                  {/* per-app usage — REAL GET /v1/analytics in the spike's chrome */}
                  <div className="panel sr">
                    <div className="panel-hd">
                      <h3>{tt('v2.account.usageT', 'Per-app usage')}</h3>
                      {usage.s === 'ready' && usage.apps.length > 0 && (
                        <span className="sub-h">
                          {tt('v2.account.usageSub', '{n} apps · {m} AI uses')
                            .replace('{n}', usage.totals.apps.toLocaleString())
                            .replace('{m}', usage.totals.interactions.toLocaleString())}
                        </span>
                      )}
                    </div>
                    {usage.s === 'loading' && (
                      <div className="panel-p stack" style="gap:10px">
                        <div className="sk sk-line" style="width:60%" />
                        <div className="sk sk-line" style="width:82%" />
                        <div className="sk sk-line" style="width:70%" />
                      </div>
                    )}
                    {usage.s === 'error' && (
                      <div className="panel-p">
                        <p className="sub-h" style="margin:0 0 12px">
                          {t('an.errLoad')}
                        </p>
                        <button
                          className="btn btn-tonal btn-sm"
                          type="button"
                          onClick={() => setUsageTick((n) => n + 1)}
                        >
                          <Ic n="history" /> {t('state.retry')}
                        </button>
                      </div>
                    )}
                    {usage.s === 'ready' && usage.apps.length === 0 && (
                      <div className="panel-p">
                        <p className="sub-h" style="margin:0 0 4px">
                          <b>{t('an.empty')}</b>
                        </p>
                        <p className="sub-h" style="margin:0 0 12px">
                          {t('an.emptySub')}
                        </p>
                        <button
                          className="btn btn-tonal btn-sm"
                          type="button"
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('pomela:new-project'));
                            goNav('');
                          }}
                        >
                          <Ic n="plus" /> {t('an.emptyCta')}
                        </button>
                      </div>
                    )}
                    {usage.s === 'ready' && usage.apps.length > 0 && (
                      <div className="list divide">
                        {usage.apps.map((a) => (
                          <div className="lrow" key={a.appId}>
                            <div className="l-txt">
                              <b>{a.name}</b>
                              <span>{usageRow(a)}</span>
                            </div>
                            <span className="l-trail tabular">
                              <span title={tt('v2.account.views', 'Views')} aria-label={tt('v2.account.views', 'Views')}>
                                <Ic n="eye" /> {a.views.toLocaleString()}
                              </span>{' '}
                              ·{' '}
                              <span
                                title={tt('v2.account.installs', 'Installs')}
                                aria-label={tt('v2.account.installs', 'Installs')}
                              >
                                <Ic n="download" /> {a.installs.toLocaleString()}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* danger zone — the REAL sign-out + typed-delete flows */}
                  <div className="panel" style="background:color-mix(in srgb,var(--danger) 6%,var(--surface))">
                    <div className="panel-hd">
                      <span
                        className="l-ico"
                        style="background:color-mix(in srgb,var(--danger) 13%,transparent);color:var(--danger)"
                      >
                        <Ic n="trash" />
                      </span>
                      <h3 style="color:var(--danger)">{t('acct.dangerZone')}</h3>
                    </div>
                    <div className="panel-p stack" style="gap:12px">
                      <p className="sub-h" style="margin:0">
                        {tt(
                          'v2.account.dangerSub',
                          'Sign out of this device, or permanently remove the data Pomela keeps here.'
                        )}
                      </p>

                      {!confirmingOut && !deleteOpen && (
                        <div className="row gap8" style="flex-wrap:wrap">
                          <button className="btn btn-ghost" type="button" onClick={() => setConfirmingOut(true)}>
                            <Ic n="arrowl" /> {t('acc.signOut')}
                          </button>
                          <button
                            className="btn btn-danger"
                            type="button"
                            onClick={() => {
                              setDeleteOpen(true);
                              setDeleteTyped('');
                              setDeleteError('');
                            }}
                          >
                            <Ic n="trash" /> {t('acct.delOpen')}
                          </button>
                        </div>
                      )}

                      {/* sign-out confirmation — Account.tsx's real two-step */}
                      {confirmingOut && (
                        <div className="stack" style="gap:10px">
                          <b style="font-size:14px">{t('acc.signOutConfirm')}</b>
                          <p className="sub-h" style="margin:0">
                            {t('acc.signOutBody')}
                          </p>
                          <div className="row gap8" style="flex-wrap:wrap">
                            <button
                              className="btn btn-danger"
                              type="button"
                              onClick={() => void doSignOut()}
                              disabled={signingOut}
                            >
                              {signingOut ? t('state.loading') : t('acc.signOut')}
                            </button>
                            <button
                              className="btn btn-ghost"
                              type="button"
                              onClick={() => setConfirmingOut(false)}
                              disabled={signingOut}
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* typed-"delete" confirmation — the real erasure flow */}
                      {deleteOpen && (
                        <div className="stack" role="region" aria-label={t('acct.delAria')} style="gap:12px">
                          <b style="font-size:14px;color:var(--danger)">{t('acct.delTitle')}</b>
                          <p
                            className="sub-h"
                            style="margin:0"
                            dangerouslySetInnerHTML={{ __html: t('acct.delBody') }}
                          />
                          <ul className="sub-h" style="margin:0;padding-inline-start:18px;display:grid;gap:4px">
                            <li>{t('acct.delList1')}</li>
                            <li>{t('acct.delList2')}</li>
                            <li>{t('acct.delList3')}</li>
                          </ul>
                          <label htmlFor="v2-account-delete-type" style="font-size:13px;font-weight:600">
                            <span dangerouslySetInnerHTML={{ __html: t('acct.delTypeLabel') }} />
                          </label>
                          <input
                            id="v2-account-delete-type"
                            className="input"
                            style="max-width:280px"
                            value={deleteTyped}
                            onInput={(e) => setDeleteTyped((e.target as HTMLInputElement).value)}
                            placeholder="delete"
                            autoComplete="off"
                            disabled={deleting}
                            aria-invalid={!!deleteError}
                          />
                          {deleteError && (
                            <p className="sub-h" role="alert" style="margin:0;color:var(--danger)">
                              {deleteError}
                            </p>
                          )}
                          <div className="row gap8" style="flex-wrap:wrap">
                            <button
                              className="btn btn-danger"
                              type="button"
                              onClick={() => void doDelete()}
                              disabled={!canConfirmDelete}
                            >
                              <Ic n="trash" /> {deleting ? t('acct.deleting') : t('acct.delConfirm')}
                            </button>
                            <button
                              className="btn btn-ghost"
                              type="button"
                              onClick={() => {
                                setDeleteOpen(false);
                                setDeleteTyped('');
                                setDeleteError('');
                              }}
                              disabled={deleting}
                            >
                              {t('acct.keepAccount')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* LOADING — the spike's skeletons, verbatim */}
          {state === 'loading' && (
            <div data-when="loading" className="stack">
              <div className="panel panel-p stack" style="gap:14px">
                <div className="row gap12">
                  <div className="sk" style="width:56px;height:56px;border-radius:16px" />
                  <div style="flex:1">
                    <div className="sk sk-line" style="width:60%;height:15px" />
                    <div className="sk sk-line" style="width:38%" />
                  </div>
                </div>
                <div className="stat-grid">
                  <div className="stat">
                    <div className="sk sk-line" style="width:60%;height:22px;margin:2px 0 8px" />
                    <div className="sk sk-line" style="width:80%" />
                  </div>
                  <div className="stat">
                    <div className="sk sk-line" style="width:44%;height:22px;margin:2px 0 8px" />
                    <div className="sk sk-line" style="width:60%" />
                  </div>
                  <div className="stat">
                    <div className="sk sk-line" style="width:50%;height:22px;margin:2px 0 8px" />
                    <div className="sk sk-line" style="width:70%" />
                  </div>
                </div>
              </div>
              <div className="sk" style="height:120px;border-radius:var(--r-card)" />
              <div className="panel panel-p stack" style="gap:12px">
                <div className="sk sk-line" style="width:30%;height:15px" />
                <div className="sk" style="height:10px;border-radius:999px" />
                <div className="row gap8">
                  <div className="sk sk-line" style="width:130px;height:34px;border-radius:10px" />
                  <div className="sk sk-line" style="width:120px;height:34px;border-radius:10px" />
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* renders BugBountyPanel's real submit/failure toasts on this screen */}
    </section>
  );
}
